import React, { useMemo, useState } from "react";
import { Box, Button, Divider } from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNew";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { useSnackbar } from "notistack";
import isNil from "lodash/isNil";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import FormSection from "../../components/finalForm/FormSection";
import RffForm from "../../components/finalForm/RffForm";
import colors from "../../theme/colors";
import {
  FORM_CARD_SX,
  FORM_FIELD_SX,
  FORM_GRID_COLUMN_GAP,
  FORM_GRID_ROW_GAP,
  FORM_SECTION_SX,
  TOGGLE_PILL_SX,
} from "../../theme/formCardStyles";
import RffBooleanCheckbox from "../../components/finalForm/inputs/RffBooleanCheckbox";
import RffBooleanToggleField from "../../components/finalForm/inputs/RffBooleanToggleField";
import RffConditional from "../../components/finalForm/shared/RffConditional";
import RffInputWrapper from "../../components/finalForm/shared/RffInputWrapper";
import {
  LINK_KIND_APPLICATION,
  useApplicationLinksHook,
} from "../../hooks/useApplicationLinksHook";
import formatAddressObjectFullAddress from "../../_src_shared/utils/functions/formatAddressObjectFullAddress";
import { prefillAllowedFor } from "../../constants/borrowerLinkPrefill";
import { renderPrefillFields } from "./prefillFieldRegistry";
import GeneratedLinksPanel from "./components/GeneratedLinksPanel";
import {
  configForLinkKind,
  configsForSelection,
  DELIVERY_EMAIL,
  DELIVERY_OPTIONS,
  DELIVERY_SHAREABLE,
  LINK_EXPIRY_OPTIONS,
  LINK_SELECTION_BOTH,
  LINK_SELECTION_OPTIONS,
  mergedFields,
  SEND_LINK_CONFIGS,
} from "./sendToBorrowerConfig";
import RffSelectToggleField from "../../components/finalForm/inputs/RffSelectToggleField";
import SendKindSelector from "./components/SendKindSelector";
import { mintShareableLinks } from "./shareableLinks";
import BrandThemeProvider from "../../components/branding/BrandThemeProvider";
import { APP_TOKENS } from "../../components/publicForm/publicFormTokens";

/**
 * Expiry / single-use / email-the-link are hidden for now — none of them are
 * enforced until the server-side link service is live (see
 * useApplicationLinksHook), so offering the controls promises behaviour the
 * link does not have. Flip to true to bring the section back; the values below
 * are what a link gets in the meantime.
 */
const SHOW_LINK_OPTIONS = false;

// Applied while SHOW_LINK_OPTIONS is false. A 30-day expiry rather than none:
// once the service IS live, links minted today should not turn out to be
// permanent.
const HIDDEN_LINK_DEFAULTS = {
  expiresInDays: 30,
  singleUse: false,
  sendEmail: false,
};

const isBlank = (v) =>
  isNil(v) ||
  v === "" ||
  (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);

/**
 * Form values -> the `prefill` object the mint endpoint accepts. Filtered
 * against the server's allowlist rather than sent wholesale: unlisted keys are
 * dropped server-side without an error, which reads as "it worked" at the call
 * site.
 */
const buildPrefill = (values, linkKind) => {
  const allowed = prefillAllowedFor(linkKind);
  const prefill = {};

  allowed.forEach((key) => {
    if (key === "subjectProperties") return;
    if (!isBlank(values[key])) prefill[key] = values[key];
  });

  // The subject property is collected as one address; the wire shape is a list
  // of property objects, and only the address survives sanitizing anyway.
  if (
    allowed.includes("subjectProperties") &&
    !isBlank(values.subjectPropertyAddress)
  ) {
    prefill.subjectProperties = [{ address: values.subjectPropertyAddress }];
  }

  return prefill;
};

/**
 * Fields that exist on a form but are WITHHELD from its link by policy.
 *
 * Deliberately not "everything that didn't travel". On "Both" the merged form
 * offers the application's loan questions, and a credit authorization has no
 * loan section at all — reporting those as withheld would be a category error,
 * and listing obvious non-applicables buries anything that matters.
 *
 * Currently EMPTY. Its only entry was primaryAddress on credit-auth links, and
 * the address is no longer collected on this screen at all, so there is nothing
 * the broker types that a link silently refuses. Kept because the next such
 * field will need exactly this: add it here AND to the matching carve-out in
 * borrowerLinkPrefill's sanitizePrefill, or the omission goes unreported.
 */
const POLICY_WITHHELD_BY_KIND = {};

const withheldPrefillFields = (values, prefill, linkKind) =>
  (POLICY_WITHHELD_BY_KIND[linkKind] || []).filter(
    (name) => !isBlank(values[name]) && !(name in prefill),
  );

/**
 * The "For <name> · <property> · <product>" line heading the result panel.
 *
 * Reads back what the broker actually entered, so a link generated for the
 * wrong borrower is obvious before it is sent rather than after. Every part is
 * optional — the line simply shortens.
 */
const summaryOf = (values, recipient) => {
  // Subject property only — the borrower's own address is no longer collected
  // here, so there is no second address to fall back to.
  const property = formatAddressObjectFullAddress(
    values.subjectPropertyAddress,
  );

  return [
    recipient?.name ? `For ${recipient.name}` : null,
    property !== "N/A" ? property : null,
    values.loanProductType || null,
  ]
    .filter(Boolean)
    .join(" · ");
};

/** How many of `names` the broker has actually filled in. */
const filledCount = (values, names = []) =>
  names.filter((name) => !isBlank((values || {})[name])).length;

/**
 * The "Opens with N of M details prefilled" line on each result card.
 *
 * Counted per KIND, against what the server will actually keep: a credit
 * authorization has no loan section, so its denominator is its own six borrower
 * fields rather than the eleven the merged "Both" form put on screen. Counting
 * what was typed instead would have a credit-auth card claim loan details its
 * form never asks for.
 *
 * `emailAddress` is excluded on purpose — it is the delivery address, always
 * present, and counting a field the broker had no choice about inflates every
 * result by one.
 */
const prefillCountsFor = (values, cfg, linkKind) => {
  const allowed = prefillAllowedFor(linkKind);
  const applicable = [
    ...(cfg?.borrowerFields || []),
    ...(cfg?.loanFields || []),
  ].filter((name) =>
    allowed.includes(
      // The one field whose form name and wire name differ — see buildPrefill.
      name === "subjectPropertyAddress" ? "subjectProperties" : name,
    ),
  );

  return {
    prefillTotal: applicable.length,
    prefilledCount: filledCount(values, applicable),
  };
};

// The borrower's own name/email ARE the link's recipient — there is no separate
// "who to send it to", so nothing is typed twice.
const recipientFrom = (values) => ({
  name: [values.firstName, values.lastName].filter(Boolean).join(" ") || null,
  email: isBlank(values.emailAddress) ? null : values.emailAddress,
});

/**
 * Mints a unique borrower-facing link (`/send/loan-application` or
 * `/send/credit-auth`, reached from the header's Send menu) and lets the broker
 * prefill what they already know before handing it over.
 *
 * Prefill is a PII boundary: SSN, date of birth, ID and bank numbers are never
 * accepted here — the server rejects the whole mint if one is present. See
 * useApplicationLinksHook.
 */
const SendToBorrowerScreen = ({
  // Everything below is normally read from the route. They're accepted as props
  // so the same screen can render inside a modal (the drilldown's borrower
  // cards) without duplicating 300 lines of form.
  linkType: linkTypeProp,
  borrowerPrefill: borrowerPrefillProp,
  embedded = false,
  // Drops the screen's own title / description / blank-form shortcuts, for a
  // host that already provides a heading.
  hideHeader = false,
  // LENDMARQ DIVERGENCE: what "Done" does on the result card. A host that owns
  // a dialog passes its close handler; routed, the screen navigates instead.
  onDone: onDoneProp,
}) => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const linkType = linkTypeProp || params.linkType;
  const config = SEND_LINK_CONFIGS[linkType];
  // Attribution is server-side: the mint stamps `owner_user_id` from the caller's
  // session, so the screen never has to supply an LO id.
  const { sendLinks, createDedicatedLink, loading } = useApplicationLinksHook();
  const { enqueueSnackbar } = useSnackbar();
  // An array — "Both" mints two. Held as a list even for one so the result
  // rendering has a single shape.
  const [generated, setGenerated] = useState(null);
  // LENDMARQ DIVERGENCE: bumped by "Send another" to remount RffForm, which is
  // how the form is blanked. `form.restart()` isn't reachable from out here —
  // the result card renders OUTSIDE the RffForm render prop, because it
  // replaces it.
  const [formKey, setFormKey] = useState(0);

  /**
   * Which link kinds to mint. Deliberately component state rather than a form
   * field: it selects a mode, it isn't borrower data, and it must not ride
   * along in the values that become the prefill payload.
   *
   * The route seeds it; changing it here does not navigate, because "Both" has
   * no route of its own.
   */
  const [selection, setSelection] = useState(linkType);

  /**
   * How the link gets to the borrower. Same reasoning as `selection`: it picks
   * a mode, so it must not ride along in the form values that become the
   * prefill payload.
   *
   * It changes which endpoint runs and how much of this form is even
   * applicable — see DELIVERY_OPTIONS. Email is the default because it is the
   * complete flow: prefill travels, and the borrower gets it without the LO
   * having to hand it over.
   */
  const [delivery, setDelivery] = useState(DELIVERY_EMAIL);
  const isShareable = delivery === DELIVERY_SHAREABLE;

  const activeConfigs = useMemo(
    () => configsForSelection(selection),
    [selection],
  );
  const isBoth = selection === LINK_SELECTION_BOTH;

  /**
   * "Both" describes itself as one email with two links, which is only true of
   * the email path — a shareable send produces two independent URLs and no
   * email at all.
   */
  const kindOptions = useMemo(
    () =>
      LINK_SELECTION_OPTIONS.map((option) =>
        option.value === LINK_SELECTION_BOTH && isShareable
          ? { ...option, description: "Two links — application first" }
          : option,
      ),
    [isShareable],
  );

  const borrowerFields = useMemo(
    () => mergedFields(activeConfigs, "borrowerFields"),
    [activeConfigs],
  );
  const loanFields = useMemo(
    () => mergedFields(activeConfigs, "loanFields"),
    [activeConfigs],
  );

  /**
   * The Loan section only exists for a selection that includes the loan
   * application — `loanFields` is empty for a credit-auth-only send, because a
   * credit authorization has no loan section and the server's allowlist refuses
   * these keys on that kind. Showing inputs that cannot travel invites the
   * broker to fill in fields the borrower will never see, so the section is
   * hidden outright rather than shown with a caveat.
   */

  /**
   * `location.state.borrowerPrefill` — a borrower handed over from somewhere
   * that already knows them (the submissions queue's "Send…" actions), so the
   * broker isn't retyping a name and email we were given on a previous form.
   * Same channel CreateLoanApplicationScreen uses.
   */
  const handedOverPrefill =
    borrowerPrefillProp || location.state?.borrowerPrefill;
  // Embedded, the caller's own close action is the way back — a "Back to loan"
  // link would navigate the page behind the modal.
  const returnTo = embedded ? null : location.state?.returnTo;

  const initialValues = useMemo(
    () => ({
      ...(SHOW_LINK_OPTIONS
        ? { expiresInDays: 30, sendEmail: true }
        : { ...HIDDEN_LINK_DEFAULTS }),
      // LENDMARQ DIVERGENCE: only the FIRST form carries the handed-over
      // borrower. "Send another" bumps `formKey`, and the whole point of it is
      // a different borrower — re-seeding the name and email of the one who was
      // just sent a link would be worse than useless.
      ...(formKey === 0 ? handedOverPrefill || {} : {}),
    }),
    [handedOverPrefill, formKey],
  );

  const hasGenerated = Boolean(generated && generated.length > 0);

  /**
   * "Send another" — a different borrower, from a blank form. Remounting via
   * `formKey` also drops any handed-over prefill, which is the point: the
   * values on screen belong to the borrower who was just sent a link.
   */
  const handleSendAnother = () => {
    setGenerated(null);
    setFormKey((k) => k + 1);
  };

  /**
   * "Done" — leave the flow. The host's own close wins when embedded (it owns
   * the dialog); otherwise return where the user came from, or fall through to
   * the queue where the submission will land.
   */
  const handleDone = () => {
    if (onDoneProp) return onDoneProp();
    if (returnTo?.path) return navigate(returnTo.path);
    return navigate(config?.submissionsPath || "/submissions");
  };

  // LENDMARQ DIVERGENCE: tpo falls back to /pipeline; this app's pipeline lives
  // at /loans.
  if (!config) return embedded ? null : <Navigate to="/loans" replace />;

  /**
   * The shareable path: the LO's own DEDICATED (evergreen) link per selected
   * kind. Nothing from the form is used — see DELIVERY_OPTIONS. It is
   * get-or-create server-side, so this is a fetch as much as a mint and
   * pressing the button twice yields the same URL.
   *
   * One call PER KIND, unlike sendLinks' single `kinds` array: the dedicated
   * endpoint is keyed on (owner, kind) and takes one. Sequential rather than
   * concurrent so a failure on the first doesn't leave a half-rendered result.
   */
  const generateShareableLinks = async () => {
    // Shared with the Submissions header's Shareable links modal, so both
    // produce the identical card from the identical call — see shareableLinks.js.
    const results = await mintShareableLinks({
      createDedicatedLink,
      configs: activeConfigs,
    });

    // All or nothing: half a "Both" result would read as a finished send with
    // one link silently missing.
    const minted = results.filter(Boolean);
    if (minted.length !== activeConfigs.length) {
      setGenerated(null);
      // HTTP failures already raise their own snackbar via useAxiosHook, so
      // only speak up when nothing else would have.
      if (minted.length > 0 || results.length === 0) {
        enqueueSnackbar(
          "Could not get your shareable link. Please try again.",
          { variant: "error" },
        );
      }
      return;
    }
    setGenerated(minted);
  };

  const onSubmit = async (values) => {
    if (isShareable) return generateShareableLinks();

    const recipient = recipientFrom(values);
    const summary = summaryOf(values, recipient);

    // ONE call mints every requested kind and emails the client — `kinds` is an
    // array, not a loop. Prefill is sent unfiltered by kind: the server applies
    // the per-kind allowlist itself (primaryAddress is refused on a credit-auth
    // link) and reports what it dropped.
    const prefill = buildPrefill(values, LINK_KIND_APPLICATION);

    await sendLinks({
      recipientEmail: recipient.email,
      recipientName: recipient.name,
      kinds: activeConfigs.map((cfg) => cfg.linkKind),
      prefill,
      note: isBlank(values.note) ? undefined : values.note,
      onSuccessFn: (res) => {
        // Each `url` here is the ONE and ONLY time the raw token is visible —
        // the server stores a sha256 and its own link_url is a display-safe
        // prefix. Losing this response means re-minting, not re-fetching.
        const minted = (res?.links || []).map((link) => {
          const cfg = configForLinkKind(link.link_kind);
          return {
            config: cfg,
            source: "server",
            link: link.url,
            linkId: link.link_id,
            tokenPrefix: link.token_prefix,
            expiresAt: link.expires_at,
            droppedFields: link.dropped_prefill_fields || [],
            notPrefilled: withheldPrefillFields(
              values,
              prefill,
              link.link_kind,
            ),
            emailedTo: res?.email_sent ? res?.recipient_email : null,
            emailError: res?.email_error || null,
            recipient,
            summary,
            ...prefillCountsFor(values, cfg, link.link_kind),
          };
        });
        setGenerated(minted);
      },
      // A guard clause that returns without requesting (a missing recipient
      // email) would otherwise leave the button looking broken — no request, no
      // snackbar, nothing. HTTP failures already raise their own snackbar via
      // useAxiosHook; this covers the ones that never reach the wire.
      onFailFn: (err) => {
        setGenerated(null);
        enqueueSnackbar(
          err?.message || "Could not generate the link. Please try again.",
          { variant: "error" },
        );
      },
    });
  };

  return (
    // LENDMARQ DIVERGENCE: brand-blue palette (this app's `primary.main` is
    // navy — see BrandThemeProvider), plus the page canvas and top padding tpo
    // gets from its dashboard shell and this app does not. Embedded, the host
    // dialog owns the surface, so neither is applied.
    <BrandThemeProvider>
    <Box
      sx={{
        ...(embedded
          ? { maxWidth: "none" }
          : {
              background: APP_TOKENS.canvas,
              minHeight: "100%",
              pt: 4,
              pb: 6,
              px: 3,
            }),
      }}
    >
    <Box sx={{ maxWidth: embedded ? "none" : 1080, mx: "auto" }}>
      <Flx column gap={3}>
        {/* Title block left, blank-form shortcuts right — they are a different
            errand from composing a link, so they sit outside the reading column
            rather than under the description. Hidden when the host already
            titles the surface (the drilldown's send modal). */}
        <Flx
          wrap
          gap={3}
          sx={{
            alignItems: "flex-end",
            justifyContent: "space-between",
            display: hideHeader ? "none" : "flex",
          }}
        >
          <Flx column gap={0.25} sx={{ minWidth: 0 }}>
            {/* Set when the broker arrived from a loan (the drilldown's Borrowers
                tab), so minting a link isn't a one-way trip out of the loan they
                were working. */}
            {returnTo?.path && (
              <Button
                variant="text"
                size="small"
                startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 17 }} />}
                onClick={() => navigate(returnTo.path)}
                sx={{ alignSelf: "flex-start", mb: 0.5, ml: -1, fontSize: 13 }}
              >
                Back to {returnTo.label || "loan"}
              </Button>
            )}
            {/* <Txt
              component="div"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.09em",
                textTransform: "uppercase",
                color: colors.slate400,
                mb: 0.5,
              }}
            >
              Origination · Send to borrower
            </Txt> */}
            <Txt component="div" variant="h2">
              Send Borrower Links
              {/* {isBoth ? "Send borrower links" : config.title} */}
            </Txt>
            <Txt
              component="div"
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 560 }}
            >
              {isShareable
                ? "Get your own reusable link. It never changes, so you can paste it into a signature, a text or a bio — and every application that comes through it is attributed to you."
                : isBoth
                  ? "Generate a loan application and a credit authorization link for the same borrower. Fill the details in once — each link gets the ones its form accepts."
                  : config.description}
            </Txt>
          </Flx>

          {/* Straight into either blank form, regardless of what's selected
              below — this is "let me just go fill one out myself", not a
              preview of the link being composed, so it does not follow the
              Loan Application / Credit Authorization / Both selection. */}
          <Flx centerY gap={1} wrap sx={{ flex: "none" }}>
            {Object.values(SEND_LINK_CONFIGS).map((cfg) => (
              <Button
                key={cfg.publicPath}
                component="a"
                href={cfg.publicPath}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                endIcon={<OpenInNewRoundedIcon sx={{ fontSize: 14 }} />}
                sx={{
                  fontSize: 13,
                  py: 1,
                  background: "#ffffff",
                  border: "1px solid #cbd4e0",
                }}
              >
                {/* "Blank" because these open the bare public form — no token,
                    no prefill, and nothing tying the submission back to the
                    broker. The generated links are the attributed ones, and the
                    distinction is easy to miss without saying so. */}
                {`Go To ${cfg.blankLabel || cfg.shortName}`}
              </Button>
            ))}
          </Flx>
        </Flx>

        {/* LENDMARQ DIVERGENCE: the result REPLACES the form rather than
            docking over it. tpo keeps the form mounted underneath and floats a
            collapsible panel at the bottom of the viewport; here the send is
            finished, so the card becomes the receipt — and the panel was
            rewritten around that, with the dock and its collapsed pill dropped
            rather than carried as dead branches. Nothing is lost: Regenerate
            brings the form back with its values intact. */}
        {hasGenerated ? (
          <GeneratedLinksPanel
            results={generated}
            summary={generated?.[0]?.summary}
            onRegenerate={() => setGenerated(null)}
            // "Send another" means a different borrower, which only exists on
            // the email path — a dedicated link is the same URL for everyone,
            // so blanking the form would offer to re-generate what is already
            // on screen.
            onSendAnother={isShareable ? undefined : handleSendAnother}
            onDone={handleDone}
          />
        ) : (
        <RffForm
          key={formKey}
          initialValues={initialValues}
          onSubmit={onSubmit}
          disableGrid
        >
          {({ handleSubmit, values: formValues }) => (
            <Box
              sx={{
                // Embedded, the dialog IS the card — keeping the border, radius
                // and shadow would draw a second card inside the first. The
                // field and toggle styles stay either way; they are what make
                // the form look the same wherever it is hosted.
                ...(embedded
                  ? { backgroundColor: "transparent" }
                  : FORM_CARD_SX),
                ...FORM_FIELD_SX,
                ...TOGGLE_PILL_SX,
              }}
            >
              <FormSection
                // sx={{ p: { xs: 2.5, md: "44px 48px 40px" } }}
                sx={{ ...FORM_SECTION_SX }}
                rowGap={FORM_GRID_ROW_GAP}
                columnGap={FORM_GRID_COLUMN_GAP}
                number={1}
                title="What are you sending?"
                description="Pick the link(s) the borrower receives."
              >
                {/* Full-row: FormSection lays its children out as 12-column
                    grid cells, and a bare control would take one column. */}
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <SendKindSelector
                    options={kindOptions}
                    value={selection}
                    onChange={setSelection}
                  />
                </Box>
              </FormSection>

              <FormSection
                sx={{ ...FORM_SECTION_SX, pt: 0 }}
                rowGap={FORM_GRID_ROW_GAP}
                columnGap={FORM_GRID_COLUMN_GAP}
                number={2}
                title="How do you want to deliver it?"
                description="Email it for them, or take the link and share it yourself."
              >
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <SendKindSelector
                    options={DELIVERY_OPTIONS}
                    value={delivery}
                    onChange={setDelivery}
                    ariaLabel="How do you want to deliver it?"
                    // Three tracks for two cards: they sit directly under the
                    // three kind cards in section 01, and matching the track
                    // width reads as one stacked set of choices rather than
                    // two unrelated grids.
                    columns={3}
                  />
                </Box>

                {/* Email path only. The address belongs with the decision about
                    what goes out, not among the optional borrower details — it
                    is the one thing here without which nothing can be sent. A
                    shareable link has no recipient at all, so asking for one
                    would be asking for a value with nowhere to go. */}
                {!isShareable && renderPrefillFields(["emailAddress"])}

                {/* Says what the shareable link IS, in place of the prefill
                    sections it replaces. All three facts are ones an LO will
                    otherwise discover the hard way: it is the same URL every
                    time, it opens blank, and it still attributes back to them. */}
                {isShareable && (
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Flx
                      gap={1.25}
                      sx={{
                        alignItems: "flex-start",
                        backgroundColor: colors.slate50,
                        border: `1px solid ${colors.slate200}`,
                        borderRadius: "8px",
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      <AutoAwesomeOutlinedIcon
                        sx={{
                          flex: "none",
                          mt: "1px",
                          fontSize: "16px !important",
                          color: `${colors.slate400} !important`,
                        }}
                      />
                      <Txt
                        component="div"
                        sx={{
                          fontSize: 12.5,
                          color: colors.slate600,
                          lineHeight: 1.55,
                        }}
                      >
                        This is <strong>your permanent link</strong> — the same
                        URL every time, reusable by as many borrowers as you
                        send it to, and it never expires. Because it is not tied
                        to one person it{" "}
                        <strong>opens as a blank form</strong>, so there is
                        nothing to prefill. Anything submitted through it is
                        still attributed to you.
                      </Txt>
                    </Flx>
                  </Box>
                )}
              </FormSection>

              {/* Everything past this point is optional prefill, and none of it
                  can be sent anywhere without an address — so it stays out of
                  the way until there is one. Opening on a single question is
                  also a far shorter thing to face than a wall of fields.

                  Gone entirely on the shareable path: a dedicated link stores
                  no prefill at all, so these inputs could not travel. Showing
                  them greyed out or with a caveat would still invite the LO to
                  fill in fields the borrower will never see. */}
              {!isShareable && (
              <RffConditional field="emailAddress" operator="notEmpty">
                {/* A banded rule rather than a heading: it separates the
                    required half of the form from the optional half, and the
                    caveat belongs on the divider itself so it is read once
                    instead of repeated in each section below. */}
                <Flx
                  centerY
                  gap={1.5}
                  sx={{
                    px: { xs: 2.5, md: "36px" },
                    py: 1.5,
                    backgroundColor: colors.slate50,
                    borderTop: `1px solid ${colors.slate200}`,
                    borderBottom: `1px solid ${colors.slate200}`,
                  }}
                >
                  <Box
                    sx={{ flex: 1, height: "1px", bgcolor: colors.slate200 }}
                  />
                  <Txt
                    component="div"
                    sx={{
                      flex: "none",
                      fontSize: 11.5,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: colors.slate500,
                    }}
                  >
                    Form prefill
                  </Txt>
                  <Txt
                    component="div"
                    sx={{
                      flex: "none",
                      fontSize: 12.5,
                      color: colors.slate400,
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    · optional — the borrower can change any of it
                  </Txt>
                  <Box
                    sx={{ flex: 1, height: "1px", bgcolor: colors.slate200 }}
                  />
                </Flx>

                {/* The borrower is entered ONCE. These values do double duty:
                    they address the link (and its email) and they prefill the
                    form. Collecting a separate "recipient" name/email alongside
                    the same prefill fields just made the broker type it twice. */}
                <FormSection
                  rowGap={FORM_GRID_ROW_GAP}
                  columnGap={FORM_GRID_COLUMN_GAP}
                  // divider
                  number={3}
                  title="Borrower Information"
                  badge={`${filledCount(formValues, borrowerFields)} filled`}
                  badgeActive={filledCount(formValues, borrowerFields) > 0}
                  // description={
                  //   isBoth
                  //     ? "Every field you fill is one less the borrower has to answer. Some fields exist on only one of the two forms; each link takes the ones it can use."
                  //     : "Every field you fill is one less the borrower has to answer."
                  // }
                  sx={{
                    ...FORM_SECTION_SX,
                    ".form-section--header": { mb: 2.5 },
                  }}
                  // sx={{ mt: 4 }}
                >
                  {renderPrefillFields(borrowerFields)}
                  {/* Its own callout rather than a clause in the description: it
                    is the answer to "why can't I prefill the SSN?", and a
                    borrower asked for one will want to see the reason stated,
                    not buried in a paragraph of instructions. */}
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Flx
                      gap={1.25}
                      sx={{
                        alignItems: "flex-start",
                        backgroundColor: "#eaf7f0",
                        // backgroundColor: "#f3f5f9",
                        // backgroundColor: colors.slate50,
                        borderRadius: "8px",
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      <ShieldOutlinedIcon
                        sx={{
                          flex: "none",
                          mt: "1px",
                          fontSize: "16px !important",
                          color: `${colors.successFg} !important`,
                        }}
                      />
                      <Txt
                        component="div"
                        sx={{ fontSize: 12.5, color: "#14532D" }}
                        // sx={{ fontSize: 12.5, color: colors.slate600 }}
                      >
                        Social security number, date of birth and ID numbers are{" "}
                        <strong>never prefilled</strong> and never travel in the
                        link.
                      </Txt>
                    </Flx>
                  </Box>
                </FormSection>
                {/* Loan application or Both only — a credit authorization has no
                    loan section, so there is nothing here that could be sent. */}
                {loanFields.length > 0 && (
                  <>
                    <Divider />
                    <FormSection
                      rowGap={FORM_GRID_ROW_GAP}
                      columnGap={FORM_GRID_COLUMN_GAP}
                      // divider
                      number={4}
                      title="Loan Information"
                      badge={`${filledCount(formValues, loanFields)} filled`}
                      badgeActive={filledCount(formValues, loanFields) > 0}
                      // description="Product and purpose tailor the application — filling them in shortens what the borrower has to answer."
                      // sx={{ mt: 4 }}
                      sx={{
                        ...FORM_SECTION_SX,
                        ".form-section--header": { mb: 2.5 },
                      }}
                    >
                      {renderPrefillFields(loanFields)}
                    </FormSection>
                  </>
                )}

                {SHOW_LINK_OPTIONS && (
                  <>
                    <Divider />
                    <FormSection
                      rowGap={FORM_GRID_ROW_GAP}
                      columnGap={FORM_GRID_COLUMN_GAP}
                      // divider
                      title="Link options"
                      sx={{ ...FORM_SECTION_SX }}
                      // sx={{ mt: 4 }}
                    >
                      {/* Nothing to email without an address, so the choice only
                      exists once one has been entered. */}
                      <RffConditional field="emailAddress" operator="notEmpty">
                        <RffBooleanToggleField
                          name="sendEmail"
                          label="Email the link to the borrower"
                          helperText="Turn this off to generate the link and share it yourself."
                          size={8}
                        />
                      </RffConditional>
                      <RffInputWrapper
                        size={4}
                        helperText="Closes the link once it has been submitted."
                      >
                        <RffBooleanCheckbox
                          name="singleUse"
                          label="Single use"
                        />
                      </RffInputWrapper>
                      <RffSelectToggleField
                        name="expiresInDays"
                        label="Link expires after"
                        options={LINK_EXPIRY_OPTIONS}
                      />
                    </FormSection>
                  </>
                )}
              </RffConditional>
              )}

              {/* OUTSIDE the reveal: the footer stays put so the screen never
                  looks like it has no way forward. The button carries the
                  disabled state and the reason instead. */}
              <Divider />
              <Box sx={{ ...FORM_SECTION_SX }}>
                <Flx between centerY wrap gap={2}>
                  <Flx centerY wrap gap={1.75} sx={{ flex: 1, minWidth: 0 }}>
                    {returnTo?.path && (
                      <Button
                        variant="text"
                        startIcon={
                          <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
                        }
                        onClick={() => navigate(returnTo.path)}
                        sx={{ ml: -1 }}
                      >
                        Back to {returnTo.label || "loan"}
                      </Button>
                    )}
                    {/* Counts what will travel on the link. A shareable link
                        carries no prefill, so the bar would sit at zero
                        forever and read as something the LO had failed to
                        do. */}
                    {!isShareable &&
                      (() => {
                      const all = [...borrowerFields, ...loanFields];
                      const filled = filledCount(formValues, all);
                      const pct = all.length ? (filled / all.length) * 100 : 0;
                      return (
                        <>
                          {/* A bar as well as the number: "6 of 11" is a fact,
                                the bar is the glance. */}
                          <Box
                            sx={{
                              display: { xs: "none", sm: "block" },
                              width: 130,
                              height: 6,
                              borderRadius: 99,
                              backgroundColor: colors.slate200,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                width: `${pct}%`,
                                height: "100%",
                                borderRadius: 99,
                                backgroundColor: "primary.main",
                                transition: "width .3s ease",
                              }}
                            />
                          </Box>
                          <Txt
                            component="div"
                            sx={{ fontSize: 13, color: colors.slate600 }}
                          >
                            <strong>
                              {filled} of {all.length}
                            </strong>{" "}
                            details prefilled
                            {/* details prefilled — the borrower fills in the rest */}
                          </Txt>
                        </>
                      );
                      })()}
                  </Flx>

                  <Flx centerY gap={1.5} sx={{ flex: "none" }}>
                    {/* Says WHY the button is dead rather than leaving the
                          broker to work it out. Disappears the moment there is
                          an address. */}
                    {/* {isBlank(formValues?.emailAddress) && (
                      <Txt
                        component="div"
                        sx={{ fontSize: 13, color: colors.slate400 }}
                      >
                        Enter the borrower's email to send
                      </Txt>
                    )} */}
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleSubmit}
                      // No recipient on the shareable path, so the address that
                      // gates the email send is not a requirement there.
                      disabled={
                        loading ||
                        (!isShareable && isBlank(formValues?.emailAddress))
                      }
                      startIcon={
                        isShareable ? (
                          <LinkRoundedIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <SendRoundedIcon sx={{ fontSize: 17 }} />
                        )
                      }
                      sx={{ minWidth: 200 }}
                    >
                      {loading
                        ? isShareable
                          ? "Getting link..."
                          : "Generating..."
                        : isShareable
                          ? isBoth
                            ? "Get Shareable Links"
                            : "Get Shareable Link"
                          : isBoth
                            ? "Generate & Email Links"
                            : "Generate & Email Link"}
                    </Button>
                  </Flx>
                </Flx>
              </Box>
            </Box>
          )}
        </RffForm>
        )}
      </Flx>
    </Box>
    </Box>
    </BrandThemeProvider>
  );
};

export default SendToBorrowerScreen;
